'use client';

import Image from '@/components/Image';
import MagicJump from '@/components/MagicJump';
import { useInViewportAnimation } from '@/hooks/useInViewportAnimation'; // 导入动画hook
import { useRef } from 'react';
import type { Swiper as SwiperType } from 'swiper';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import styles from './common.module.scss';

interface Part2Item {
  img: string;
  title: string;
  desc: string;
  title_color?: string;
  desc_color?: string;
  jump_target?: any;
}

interface Part2Data {
  title: string;
  content: string;
  list: Part2Item[];
}

interface Part2Props {
  data: Part2Data | null;
  isMobile: boolean;
}

export default function Part2({ data, isMobile }: Part2Props) {
  const swiperRef = useRef<SwiperType | null>(null);

  // 动画hook，仅PC端执行
  const animationRef = useInViewportAnimation(
    () => {
      if (isMobile) return;
      try {
        // 标题动画
        const PartArr = [
          '.fit-guide-container-section-part2-title',
          '.fit-guide-container-section-part2-content',
        ];
        PartArr.forEach((item) => {
          const element = document.querySelector(item);
          if (element) {
            element.classList.add('fadeInDownAnimate');
          }
        });
        // 卡片动画
        const cards = document.getElementsByClassName(
          'fit-guide-container-section-part2-swiper-item',
        );
        Array.from(cards).forEach((item, idx) => {
          setTimeout(() => {
            item.classList.add('activityFadeInUpAnimate');
          }, idx * 120);
        });
      } catch {
        // 出错时直接显示
        const elements = document.querySelectorAll('.opacity0');
        elements.forEach((el) => {
          const element = el as HTMLElement; // 创建一个新的变量
          element.style.opacity = '1'; // 修改新变量的属性
        });
      }
    },
    { threshold: 0.1, once: true },
  );

  if (!data || !data.list?.length) {
    return null;
  }

  return (
    <div className="mt-[40px] md:mt-0 bg-[#eee] pt-[40px] pb-[28px] px-0 md:pt-[2.92vw] md:px-[2.5vw] md:pb-[3.75vw]">
      <h2
        className={`fit-guide-container-section-part2-title ${styles.section_title} ${styles['max-line-1']} ${!isMobile ? 'opacity0' : ''}`}
        dangerouslySetInnerHTML={{ __html: data.title }}
      />
      <div
        className={`fit-guide-container-section-part2-content ${styles.section_desc} ${isMobile ? styles['max-line-3'] : styles['max-line-1']} ${!isMobile ? 'opacity0' : ''}`}
        dangerouslySetInnerHTML={{ __html: data.content }}
      />

      {isMobile ? (
        // 移动端轮播
        <div className="mt-[24px]">
          <Swiper spaceBetween={10} slidesPerView="auto" className="!pl-[14px]">
            {data.list.map((item, index) => (
              <SwiperSlide key={index} className="!w-[275px] mr-[10px]">
                <MagicJump
                  prefetch={false}
                  item={item.jump_target}
                  className="relative block no-underline"
                >
                  <Image
                    src={item.img}
                    alt={item.title}
                    width={200}
                    height={200}
                    className="block w-full h-auto !rounded-[10px] overflow-hidden aspect-[1100/1322]"
                  />
                  <div className="absolute left-0 bottom-[14px] px-[14px] w-full box-border">
                    <h3
                      className={`text-[16px] leading-[20px] mb-[10px] ${styles['max-line-1']}`}
                      style={{
                        color: item.title_color,
                        fontFamily: 'var(--font-bricolage-bold)',
                      }}
                      dangerouslySetInnerHTML={{ __html: item.title }}
                    />
                    <div
                      className={`text-[12px] leading-[18px] ${styles['max-line-3']}`}
                      style={{
                        color: item.desc_color,
                        fontFamily: 'BricolageGrotesque',
                      }}
                      dangerouslySetInnerHTML={{ __html: item.desc }}
                    />
                  </div>
                </MagicJump>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      ) : (
        // PC端轮播
        <div
          className="relative mt-[2.08vw]"
          ref={(node) => {
            // 为动画设置ref（仅PC端）
            if (!isMobile && animationRef && typeof animationRef !== 'function') {
              (animationRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
            }
          }}
        >
          <Swiper
            spaceBetween={24}
            slidesPerView={4}
            modules={[Navigation]}
            navigation={{
              prevEl: '.photo-prev',
              nextEl: '.photo-next',
            }}
            onBeforeInit={(swiper) => {
              swiperRef.current = swiper;
            }}
          >
            {data.list.map((item, index) => (
              <SwiperSlide
                key={index}
                className={`fit-guide-container-section-part2-swiper-item opacity0 ${index < 4 ? styles.hasAnimate : ''}`}
              >
                <MagicJump
                  prefetch={false}
                  item={item.jump_target}
                  className="relative block no-underline"
                >
                  <Image
                    src={item.img}
                    alt={item.title}
                    width={200}
                    height={200}
                    className="block w-full h-auto !rounded-[16px] overflow-hidden aspect-[438/526]"
                  />
                  <div
                    className={`absolute left-0 bottom-[24px] px-[2.5vw] w-full box-border ${item.jump_target?.jump_type ? 'cursor-pointer' : ''}`}
                  >
                    <h3
                      className={`text-[max(1.25vw,16px)] leading-[max(1.46vw,16px)] mb-[10px] ${styles['max-line-1']}`}
                      style={{
                        color: item.title_color,
                        fontFamily: 'var(--font-bricolage-bold)',
                      }}
                      dangerouslySetInnerHTML={{ __html: item.title }}
                    />
                    <div
                      className={`text-[max(0.833vw,14px)] leading-[max(1.25vw,14px)] mt-[0.625vw] h-[2.5vw] ${styles['max-line-2']}`}
                      style={{
                        color: item.desc_color,
                        fontFamily: 'BricolageGrotesque',
                      }}
                      dangerouslySetInnerHTML={{ __html: item.desc }}
                    />
                  </div>
                </MagicJump>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      )}
    </div>
  );
}
